import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, useTheme, Paper, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import { listAccuracyReports, deleteAccuracyReport } from '../../api/runningApi';
import { apiErrorMessage } from '../../utils/apiError';

const fmtSecs = (s) => {
  if (s == null) return '—';
  const a = Math.abs(s);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = Math.round(a % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtSignedSecs = (s) => (s == null ? '—' : (s < 0 ? '-' : '+') + fmtSecs(s));

export default function RunningAccuracyReports() {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    listAccuracyReports()
      .then(setReports)
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load reports')))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this accuracy report?')) return;
    try {
      await deleteAccuracyReport(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Delete failed'));
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box p={{ xs: 1.5, md: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={0.5}>Prediction Accuracy Reports</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Saved comparisons of past predictions vs their actual races — for debugging and tuning the prediction algorithm.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {reports.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reports yet. Open a race analysis and use “Grade a prediction”.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[800] }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prediction</TableCell>
                <TableCell>Reference FIT</TableCell>
                <TableCell>Actual</TableCell>
                <TableCell align="right">Dist</TableCell>
                <TableCell align="right">Predicted</TableCell>
                <TableCell align="right">Actual</TableCell>
                <TableCell align="right">Error</TableCell>
                <TableCell align="center">Match</TableCell>
                <TableCell align="right">Date</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const matchColor = { good: 'success', warn: 'warning', different: 'error' }[r.course_match] || 'default';
                return (
                  <TableRow
                    key={r.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/running/prediction-accuracy/${r.id}`)}
                  >
                    <TableCell>{r.label_prediction}</TableCell>
                    <TableCell sx={{ color: colors.grey[400] }}>{r.prediction_fit_filename || '—'}</TableCell>
                    <TableCell>{r.label_actual}</TableCell>
                    <TableCell align="right">{r.race_distance_km != null ? `${r.race_distance_km} km` : '—'}</TableCell>
                    <TableCell align="right">{fmtSecs(r.predicted_total_time_s)}</TableCell>
                    <TableCell align="right">{fmtSecs(r.actual_total_time_s)}</TableCell>
                    <TableCell align="right" sx={{ color: r.total_time_error_s < 0 ? colors.redAccent[300] : colors.greenAccent[300] }}>
                      {fmtSignedSecs(r.total_time_error_s)}
                      {r.total_time_error_pct != null ? ` (${r.total_time_error_pct}%)` : ''}
                    </TableCell>
                    <TableCell align="center"><Chip label={r.course_match || '—'} color={matchColor} size="small" /></TableCell>
                    <TableCell align="right">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="right" padding="checkbox">
                      <Tooltip title="Delete report">
                        <IconButton size="small" color="error" onClick={(e) => handleDelete(r.id, e)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
